"use client"

import axios from "axios"
import moment from "moment"
import React, { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"
import Select from "react-select"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import sortAsce from "../../assets/svgs/sort-ascending.svg"
import sortDesc from "../../assets/svgs/sort-descending.svg"
import swap from "../../assets/svgs/swap-vertical.svg"
import UpArrow from "../../assets/svgs/up-arrow.svg"
import ActionsMenu from "../../components/ActionsMenu"
import ConfirmationModalBov from "../../components/ConfirmationModals/ConfirmationModalBov"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import Loader from "../../components/Loader"
import ContactPagination from "../../components/Pagination/ContactPagination"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import LeadDataContainer from "../../pages/Leads/components/LeadDataContainer"
import { BASE_URL } from "../../utils/Element"
import { initialPipelineFilterData } from "../../utils/initialData"
import DispositionsFunnelChart from "./Component/DispositionsFunnelChart"
import PyramidChart from "./Component/PyramidChart"
import StageDispositionDashboard from "./Component/StageDahboradDisposition"
import StageDashboard from "./Component/StageDashboard"
import AddPiplineModal from "./Modal/AddPiplineModal"
import EditpipelineModal from "./Modal/EditpipelineModal"
import PipelineFilter from "./PipelineFilter"

const Pipeline = () => {
  const [config] = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)

  const [loading, setLoading] = useState(true)
  const [leadsData, setLeadsData] = useState([])
  const [selectedId, setSelectedId] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [expandedRows, setExpandedRows] = useState({})
  const [pipelineDashboard, setPipelineDashboard] = useState()
  const [itemPerPage, setItemPerPage] = useState(50)
  const [sortBy, setSortBy] = useState("id")
  const [sortDirection, setSortDirection] = useState("asc")
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [disableDelete, setDisableDelete] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [bdsOptions, setBdsOptions] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)
  const [isSwitchingUser, setIsSwitchingUser] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const [activeStageTab, setActiveStageTab] = useState("Acquisition")
  const [showConfirmModalBov, setShowConfirmModalBov] = useState(false)
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialPipelineFilterData
  })

  const [filterStatus, setFilterStatus] = useState({
    total_pipeline: false,
    lead_not_contacted: false,
    buyer_making_offers_card: false,
    pipeline_bov_presented_count: false,
    total_potential_gci: false,
    new_lead_acquisition: false,
    show_property: false,
    buyer_making_offers_acquisition: false,
    executed_loi_acquisition: false,
    under_contract_acquisition: false,
    new_lead_dispositions: false,
    bov_presented_dispositions: false,
    exclusive_listing_dispositions: false,
    executed_loi_dispositions: false,
    under_contract_dispositions: false,
  })

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      // Only update if empty or ≥ 3 characters
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  // ─── Build query with debounced search ────────────────────────
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`

    // Use debouncedSearch here
    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const {
      contact,
      associate_id,
      transaction_type,
      stage,
      min_potential_gcl,
      max_potential_gcl,
      last_contact,
      lastContactSentDate,
      lastContactSentSdate,
      lastContactSentEdate,
      within_last_day,
      date_category,
      longer_than_day,
    } = filters

    if (contact?.id) params += `&contact=${encodeURIComponent(contact.id)}`
    if (associate_id) params += `&associate_id=${encodeURIComponent(associate_id)}`
    if (transaction_type) params += `&transaction_type=${encodeURIComponent(transaction_type)}`
    if (stage) params += `&stage=${encodeURIComponent(stage)}`
    if (min_potential_gcl) params += `&min_potential_gcl=${encodeURIComponent(min_potential_gcl)}`
    if (max_potential_gcl) params += `&max_potential_gcl=${encodeURIComponent(max_potential_gcl)}`
    if (last_contact !== undefined) params += `&last_contact=${encodeURIComponent(last_contact)}`
    if (lastContactSentDate) params += `&last_update_date=${encodeURIComponent(lastContactSentDate)}`
    if (lastContactSentSdate) params += `&last_update_start_date=${encodeURIComponent(lastContactSentSdate)}`
    if (lastContactSentEdate) params += `&last_update_end_date=${encodeURIComponent(lastContactSentEdate)}`
    if (date_category) params += `&date_category=${encodeURIComponent(date_category)}`
    if (within_last_day) params += `&within_last_day=${encodeURIComponent(within_last_day)}`
    if (longer_than_day) params += `&longer_than_day=${encodeURIComponent(longer_than_day)}`

    if (sortDirection && sortBy !== "id") {
      params += `&sort_direction=${sortDirection}`
    }
    if (sortBy && sortBy !== "id") {
      params += `&sort_by=${sortBy}`
    }

    // Filter status flags
    if (filterStatus.total_pipeline) params += `&total_pipeline=true`
    if (filterStatus.lead_not_contacted) params += `&lead_not_contacted=true`
    if (filterStatus.buyer_making_offers_card) params += `&buyer_making_offers_card=true`
    if (filterStatus.pipeline_bov_presented_count) params += `&pipeline_bov_presented_count=true`
    if (filterStatus.total_potential_gci) params += `&total_potential_gci=true`
    if (filterStatus.new_lead_acquisition) params += `&new_lead_acquisition=true`
    if (filterStatus.show_property) params += `&show_property=true`
    if (filterStatus.buyer_making_offers_acquisition) params += `&buyer_making_offers_acquisition=true`
    if (filterStatus.executed_loi_acquisition) params += `&executed_loi_acquisition=true`
    if (filterStatus.under_contract_acquisition) params += `&under_contract_acquisition=true`
    if (filterStatus.new_lead_dispositions) params += `&new_lead_dispositions=true`
    if (filterStatus.bov_presented_dispositions) params += `&bov_presented_dispositions=true`
    if (filterStatus.exclusive_listing_dispositions) params += `&exclusive_listing_dispositions=true`
    if (filterStatus.executed_loi_dispositions) params += `&executed_loi_dispositions=true`
    if (filterStatus.under_contract_dispositions) params += `&under_contract_dispositions=true`

    return params
  }

  const fetchPipeline = (filters = filterData) => {
    const queryParams = buildQueryParams(filters)
    setLoading(true)

    axios
      .get(`${BASE_URL}/pipelines?active_tab=${activeTab}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.pipelinesList
        setLeadsData(value?.data || [])
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // ─── Main triggers ────────────────────────────────────────────
  useEffect(() => {
    fetchPipeline(filterData)
  }, [
    sortDirection,
    sortBy,
    filterStatus,
    activeTab,
    currentPage,
    itemPerPage,
    debouncedSearch,     // ← important
    selectedUser,        // if it affects list
  ])

  // Reset page when important filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab, filterStatus, selectedUser])

  const onSuccess = (filters) => {
    fetchPipeline(filters || filterData)
  }

  // ─── Rest of your handlers (unchanged except where needed) ────

  const handleNext = () => {
    if (currentPage < paginationData.totalPages) {
      setCurrentPage(currentPage + 1)
    }
  }

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1)
    }
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-pipeline/${selectedId}?type=1`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const handleConvertBov = () => {
    axios
      .get(`${BASE_URL}/bov-convert/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowConfirmModalBov(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const fetchDashboardData = () => {
    let url = `${BASE_URL}/dashboard-pipeline?active_tab=${activeTab}`
    if (selectedUser?.value) {
      url += `&show_user_id=${selectedUser.value}`
    }

    axios
      .get(url, config)
      .then((res) => setPipelineDashboard(res?.data?.pipeline_dashboard))
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const fetchUsers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=users&role_id=3`, config)
      .then((res) => {
        const options = res?.data?.data?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
          role: el.role_id,
        }))
        setBdsOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  useEffect(() => {
    if ([0, 3].includes(activeTab)) {
      fetchDashboardData()
    }
  }, [selectedUser, activeTab])

  const handlePipelineBoxClick = (statusKey) => {
    let targetTab = activeTab === 0 ? 2 : 5

    setActiveTab(targetTab)
    setCurrentPage(1)
    setFilterStatus({
      total_pipeline: false,
      lead_not_contacted: false,
      buyer_making_offers_card: false,
      pipeline_bov_presented_count: false,
      total_potential_gci: false,
      [statusKey]: true,
    })
  }

  const handleFunnelChartClick = (filterKeys = {}) => {
    let targetTab = activeTab === 0 ? 2 : 5

    const reset = {
      new_lead_acquisition: false,
      show_property: false,
      buyer_making_offers_acquisition: false,
      executed_loi_acquisition: false,
      under_contract_acquisition: false,
      new_lead_dispositions: false,
      bov_presented_dispositions: false,
      exclusive_listing_dispositions: false,
      executed_loi_dispositions: false,
      under_contract_dispositions: false,
    }

    setActiveTab(targetTab)
    setCurrentPage(1)
    setFilterStatus({ ...reset, ...filterKeys })
  }

  const handleUserSwitch = (option) => {
    if (!option) {
      setSelectedUser(null)
      setIsSwitchingUser(false)
      onSuccess()
      return
    }

    if (!isSwitchingUser) {
      setOriginalUserData(leadsData)
      setIsSwitchingUser(true)
    }

    setSelectedUser(option)
    onSuccess()
  }

  const getDisplayText = (el) => {
    if (el?.lead?.lead_type === 0 && el?.lead?.portfolio !== 1) {
      if (!el?.lead?.link?.property_name || !el?.lead?.link_address_name) {
        return "Property details unavailable"
      }
      return (
        <>
          <span>{el.lead?.link.property_name}</span>
          <br />
          <span>{el.lead?.link_address_name.street}</span>
          <br />
          <span>
            {el.lead?.link_address_name.city}, {el.lead?.link_address_name.state}
          </span>
        </>
      )
    }
    if (el?.lead?.lead_type === 1 && el.lead?.portfolio !== 1) {
      return el?.lead?.link?.property_type?.[0]?.type || ""
    }
    return el?.lead?.link?.name || ""
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  // ─── JSX ──────────────────────────────────────────────────────
  return (
    <BaseLayout>
      <div className="contact-header">
        <p className="head-1 dark-H">Pipeline</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center">
            <PipelineFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Pipeline"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts">
              <input
                type="text"
                className="body-S"
                placeholder="Search..."
                value={search}
                onChange={handleSearchChange}   // ← updated handler
                style={{ border: "1px solid #D8D8D8", marginTop: "0px" }}
              />
            </div>

            {(activeTab === 1 || activeTab === 3 || activeTab === 4) && (
              <div className="relative ml-2">
                <Select
                  className="body-S min-w-[200px]"
                  placeholder="Sales users"
                  value={selectedUser}
                  options={bdsOptions}
                  onChange={handleUserSwitch}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="task-tabs mx-1 mt-2 flex justify-between body-L dark-M mb-4">
        <div className="flex gap-4">
          {[" My Dashboard", "My Stage ", "My list ", "Team Dashboard", "Team Stage", "Team List"].map((tab, index) => (
            <p
              key={index}
              role="button"
              className={`${activeTab === index ? "head-4 dark-H active" : ""} py-1`}
              onClick={() => {
                setActiveTab(index)
                setCurrentPage(1)
                setFilterStatus({
                  total_pipeline: false,
                  lead_not_contacted: false,
                  buyer_making_offers_card: false,
                  pipeline_bov_presented_count: false,
                  total_potential_gci: false,
                  new_lead_acquisition: false,
                  show_property: false,
                  buyer_making_offers_acquisition: false,
                  executed_loi_acquisition: false,
                  under_contract_acquisition: false,
                  new_lead_dispositions: false,
                  bov_presented: false,
                  exclusive_listing: false,
                  executed_loi_dispositions: false,
                  under_contract_dispositions: false,
                })
              }}
            >
              {tab}
            </p>
          ))}
        </div>
      </div>

      {(activeTab === 2 || activeTab === 5) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : leadsData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Pipline Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th>
                      <p>Details</p>
                    </th>
                    <th className="green-H">
                      <div className="table-header">
                        DATE ADDED
                        <img
                          role="button"
                          src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          onClick={() => {
                            setSortBy("created_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                          alt="icon"
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        LAST CONTACTED
                        <img
                          role="button"
                          src={sortBy !== "last_update_date" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("last_update_date")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        CONTACT NAME
                        <img
                          role="button"
                          src={sortBy !== "contact_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("contact_id")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        TRANSACTION TYPE
                        <img
                          role="button"
                          src={sortBy !== "transaction_type" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("transaction_type")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    {/* <th>
                      <div className="table-header">
                        Portfolio
                        <img
                          role="button"
                          src={sortBy !== "portfolio" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("portfolio")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th> */}

                    <th>
                      <div className="table-header">
                        STAGE
                        <img
                          role="button"
                          src={sortBy !== "stage" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("stage")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    <th>
                      <div className="table-header">
                        POTENTIAL GCI
                        <img
                          role="button"
                          src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("updated_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>

                    {/* <th>
                      <div className="table-header">
                        ASSOCIATE
                        <img
                          role="button"
                          src={sortBy !== "associate_id" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("associate_id")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th> */}

                    <th>
                      <div className="table-header">
                        Property /
                        <br />
                        Acquisition Criteria
                        <img
                          role="button"
                          src={sortBy !== "updated_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce}
                          alt="icon"
                          onClick={() => {
                            setSortBy("updated_at")
                            if (sortDirection === "desc") {
                              setSortDirection("asc")
                            } else {
                              setSortDirection("desc")
                            }
                          }}
                        />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">Actions</div>
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {leadsData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr key={idx} className="dark-H body-N">
                        <td>
                          <img
                            role="button"
                            src={expandedRows[el.id] ? UpArrow : DownArrow}
                            alt="icon"
                            className="sidebar-icons"
                            onClick={() => toggleRowDetails(el.id)}
                          />
                        </td>
                        <td className="dark-M">{moment(el?.created_at).tz("America/New_York").format("MM/DD/YY")}</td>
                        <td className="dark-M">
                          {el?.formatted_created_at
                            ? moment(el?.formatted_created_at).tz("America/New_York").format("MM/DD/YY")
                            : ""}{" "}
                          <span className="dark-M body-S">
                            {el?.formatted_created_at ? ` (${moment(el?.formatted_created_at).fromNow()})` : ""}
                          </span>
                        </td>

                        <td className="capitalize head-5">
                          <Link
                            to={`/contact/${el?.contact?.id}`}
                            onClick={(e) => {
                              e.preventDefault()
                              if (el?.contact?.id) {
                                navigate(`/contact/${el?.contact?.id}`)
                              }
                            }}
                            rel="noopener noreferrer"
                          >
                            {el?.contact?.first_name} {el?.contact?.last_name}
                          </Link>
                        </td>
                        <td className="dark-M">{el?.transaction_type_name}</td>
                        {/* <td className="dark-M items-center">{el?.lead?.portfolio ? "Yes" :  "No"}</td> */}
                        <td className="dark-M">{el?.stage_name}</td>
                        <td className="dark-M">{el?.potential_gcl_format ? `$${el.potential_gcl_format}` : ""}</td>

                        <td className="head-5 capitalize">
                          <Link
                            to={
                              el?.portfolio === 0
                                ? `/property/${el?.property_id}`
                                : el?.portfolio === 1 && el?.lead?.link?.owner_info_type === "contact"
                                  ? `/contact/${el?.lead?.link?.owner?.id}`
                                  : el?.portfolio === 1 && el?.lead?.link?.owner_info_type === "company"
                                    ? `/company/${el?.lead?.link?.owner?.id}`
                                    : el?.lead?.lead_type === 0
                                      ? `/property/${el?.lead?.link?.id}`
                                      : el?.lead?.lead_type === 1
                                        ? `/buyer/${el?.lead?.link?.id}`
                                        : "#"
                            }
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();

                              if (el?.portfolio === 0) {
                                navigate(`/property/${el?.property_id}`);
                              } else if (el?.portfolio === 1 && el?.lead?.link?.owner_info_type === "contact") {
                                navigate(`/contact/${el?.lead?.link?.owner?.id}`);
                              } else if (el?.portfolio === 1 && el?.lead?.link?.owner_info_type === "company") {
                                navigate(`/company/${el?.lead?.link?.owner?.id}`);
                              } else if (el?.lead?.lead_type === 0) {
                                navigate(`/property/${el?.lead?.link?.id}`);
                              } else if (el?.lead?.lead_type === 1) {
                                navigate(`/buyer/${el?.lead?.link?.id}`);
                              }
                            }}
                          >
                            {getDisplayText(el)}
                          </Link>

                        </td>

                        <td className="flex gap-3 items-center w-full justify-center">
                          <ActionsMenu
                            from="pipeline"
                            handleEdit={() => {
                              setShowEditModal(true)
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id)
                              setShowDeleteModal(true)
                            }}
                            otherBovTitle={"Convert to BOV"}
                            handleConvertBov={() => {
                              setShowConfirmModalBov(true)
                              setSelectedId(el?.id)
                            }}
                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="flex items-center gap-6 mb-7">

                                <div>
                                  <label className="dark-H head-4 block">Associated</label>
                                  <p className="body-N dark-H pt-2 min-h-[1.5rem]">
                                    <span className="capitalize head-5">
                                      {el?.associate?.length > 0 ? (
                                        el.associate.map((person) => (
                                          <div key={person.id}>
                                            <Link
                                              to={`/user/${person.id}`}
                                              onClick={(e) => {
                                                e.preventDefault()
                                                navigate(`/user/${person.id}`)
                                              }}
                                              rel="noopener noreferrer"
                                              className="block text-gray-800 hover:text-green-700"
                                            >
                                              {person.first_name} {person.last_name}
                                            </Link>
                                          </div>
                                        ))
                                      ) : (
                                        <span>-</span>
                                      )}
                                    </span>
                                  </p>
                                </div>
                              </div>

                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {leadsData?.length > 0 && (
            <ContactPagination
              activeTab={activeTab}
              paginationData={paginationData}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value)
                setCurrentPage(1)
              }}
            />
          )}
        </div>
      )}

      {(activeTab === 0 || activeTab === 3) && (
        <>
          <div className="flex items-stretch gap-3">
            <LeadDataContainer
              text="TOTAL CONTACTS IN PIPELINE"
              number={pipelineDashboard?.pipeline_contact_count}
              className="h-40 w-full"
              onClick={() => handlePipelineBoxClick("total_pipeline")}
            />

            <LeadDataContainer
              text="TOTAL QUALIFIED PROSPECTS NOT CONTACTED IN 14+ DAYS"
              number={pipelineDashboard?.pipeline_new_lead_count}
              className="h-40 w-full"
              onClick={() => handlePipelineBoxClick("lead_not_contacted")}
            />

            <LeadDataContainer
              text="TOTAL BUYERS MAKING OFFERS NOT CONTACTED IN 14+ DAYS"
              number={pipelineDashboard?.pipeline_buyer_making_offers_count}
              className="h-40 w-full"
              onClick={() => handlePipelineBoxClick("buyer_making_offers_card")}
            />

            <LeadDataContainer
              text="TOTAL BOV PRESENTED NOT CONTACTED IN 14+ DAYS"
              number={pipelineDashboard?.pipeline_bov_presented_count}
              className="h-40 w-full"
              onClick={() => handlePipelineBoxClick("pipeline_bov_presented_count")}
            />

            <LeadDataContainer
              text="TOTAL POTENTIAL GCI"
              number={pipelineDashboard?.bov_potential_gcl ? `$${pipelineDashboard.bov_potential_gcl}` : ""}
              className="h-40 w-full"
              onClick={() => handlePipelineBoxClick("total_potential_gci")}
            />
          </div>

          <div className="px-4 flex justify-center items-start gap-4 bg-white pb-[6rem]">
            <div className="w-[50%]">
              <PyramidChart
                Title="TOTAL ACQUISITIONS"
                Total={pipelineDashboard?.pipeline_status_acquisition_count}
                activeTab={activeTab}
                onClick={(filterKeys) => handleFunnelChartClick(filterKeys)}
              />
            </div>

            <div className="w-[50%]">
              <DispositionsFunnelChart
                Title="TOTAL DISPOSITIONS"
                Total={pipelineDashboard?.pipeline_status_disposition_count}
                activeTab={activeTab}
                onClick={(filterKeys) => handleFunnelChartClick(filterKeys)}
              />
            </div>
          </div>
        </>
      )}

      {(activeTab === 1 || activeTab === 4) && (
        <div>
          <div className="mt-3">
            <div className="flex justify-between header-tabs rounded-full bg-white p-1 max-w-xs mx-auto">
              <button
                onClick={() => setActiveStageTab("Acquisition")}
                className={`${activeStageTab === "Acquisition" ? "green-bg-H text-white font-medium" : "text-gray-600"
                  } px-4 py-2 rounded-full flex-1 text-sm transition-all`}
              >
                Acquisitions
              </button>
              <button
                onClick={() => setActiveStageTab("Disposition")}
                className={`${activeStageTab === "Disposition" ? "green-bg-H text-white font-medium" : "text-gray-600"
                  } px-4 py-2 rounded-full flex-1 text-sm transition-all`}
              >
                Dispositions
              </button>
            </div>
          </div>

          <div className="p-4">
            {activeStageTab === "Acquisition" && <StageDashboard activeTab={activeTab} selectedUser={selectedUser} />}
            {activeStageTab === "Disposition" && (
              <StageDispositionDashboard activeTab={activeTab} selectedUser={selectedUser} />
            )}
          </div>
        </div>
      )}

      <AddPiplineModal
        from="pipeline"
        showModal={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCallApi={onSuccess}
      />

      <EditpipelineModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        from="pipeline"
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
      />

      <ConfirmationModalBov
        showModal={showConfirmModalBov}
        onClose={() => setShowConfirmModalBov(false)}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
        id={selectedId}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
        fromPipeline={true}
      />
    </BaseLayout>
  )
}

export default Pipeline